import { useState, useCallback } from "react";
import type { OrderWithItems } from "@/types/pos";
import { Transaction } from "@/constants";
import { ChevronLeft, MapPin, Flame, Leaf, Flag, ExternalLink, Wallet } from "lucide-react";
import placeholder from "@/assets/pos/placeholder.png";
import { STATUS_MAP, STATUS_STEPS } from "@/lib/pos/global";
import TransactionDetailDialog from "@/components/TransactionDetailDialog";
import { useUser } from "@/hooks/use-user";
import { useToast } from "@/hooks/use-toast";
import { apiFetch } from "@/utils";
import CardBrandIcon from "@/components/CardBrandIcon";

interface OrderDetailProps {
  order: OrderWithItems;
  onClose: () => void;
}

export default function OrderDetail({ order, onClose }: OrderDetailProps) {
  const { uid } = useUser();
  const { toast } = useToast();

  const isCard = order.paymentMethod === 'CARD';

  const formatAmount = (usdAmount: number) => {
    if (isCard) return `$${usdAmount.toFixed(2)}`;
    const rate = order.sechExchangeRate ? parseFloat(order.sechExchangeRate) : null;
    if (!rate) return `$${usdAmount.toFixed(2)}`;
    return `${(usdAmount / rate).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 4 })} SECH`;
  };
  const [selectedTransaction, setSelectedTransaction] = useState<Transaction | null>(null);
  const [isLoadingTransaction, setIsLoadingTransaction] = useState(false);

  const handleTransactionClick = useCallback(async (transactionId: number) => {
    if (!uid || isLoadingTransaction) return;
    setIsLoadingTransaction(true);
    try {
      const res = await apiFetch(`/v1/transactions/${uid}`, { withCredentials: true });
      if (!res.ok) throw new Error("Failed to fetch transactions");
      const json = await res.json();
      const transactions: Transaction[] = json?.data?.items ?? json;
      const match = transactions.find((tx) => tx.id === transactionId);
      if (match) {
        setSelectedTransaction(match);
      } else {
        toast({ title: "Not Found", description: "Transaction not found.", variant: "destructive" });
      }
    } catch {
      toast({ title: "Error", description: "Failed to load transaction details.", variant: "destructive" });
    } finally {
      setIsLoadingTransaction(false);
    }
  }, [uid, isLoadingTransaction, toast]);

  const getCurrentStepIndex = (status?: string) => {
    const s = status?.toLowerCase() || "pending";

    let idx = STATUS_STEPS.findIndex((st) => st.key === s);
    if (idx === -1) idx = 0;

    return idx;
  };

  return (
    <div className="w-full max-w-5xl h-full mx-auto px-5 pb-10 bg-[var(--pos-secondary-background-color)] overflow-auto">
      {/* Header */}
      <div className="flex items-center gap-3 py-4">
        <button
          type="button"
          aria-label="Go back"
          onClick={onClose}
          className="h-8 w-8 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 focus:outline-none focus:ring-2 focus:ring-white/40"
        >
          <ChevronLeft className="h-[22px] w-[22px] text-[#ffffff]" />
        </button>
        <h1 className="text-[20px] leading-[18px] font-bold text-[var(--pos-primary-text-color)] font-montserrat">Order Detail</h1>
      </div>

      <div className="space-y-2 pb-[62px]">
        <div
          key={order.id}
          className="bg-[var(--pos-primary-background-color)] rounded-sm py-3 px-4"
        >
          {/* STATUS BAR (icons + active/fill/not-fill) */}
          {!(order.status?.toLowerCase() === "delivered") && (
            <div className="border-b border-dashed border-[var(--pos-layout-border-color)] w-full mb-4 pb-4">
              <div className="flex justify-between relative">
                {STATUS_STEPS.map((step, index) => {
                  const Icon = step.Icon;
                  const state =
                    index < getCurrentStepIndex(order.status)
                      ? "filled"
                      : index === getCurrentStepIndex(order.status)
                      ? "active"
                      : "notFilled";

                  const style = {
                    filled:
                      "text-green-400 bg-green-900/30",
                    active:
                      "text-yellow-400 bg-yellow-900/30",
                    notFilled: "text-[var(--pos-disabled-text-color)] bg-[var(--pos-accordian-disabled-btn-background)]",
                  }[state];

                  return (
                    <div
                      key={step.key}
                      className="flex flex-col items-center w-[27%] relative first:ml-[calc(-12.5%+24px)] last:mr-[calc(-12.5%+26px)]"
                    >
                      {/* connecting line */}
                      {index !== STATUS_STEPS?.length - 1 && (
                        <div
                          className={`absolute top-[15px] left-1/2 h-[1px] w-full -translate-y-1/2 ${
                            index < getCurrentStepIndex(order.status)
                              ? "bg-green-500"
                              : "bg-[var(--pos-accordian-disabled-btn-background)]"
                          }`}
                        />
                      )}

                      {/* circle + icon */}
                      <div
                        className={`rounded-full h-[26px] w-[26px] flex items-center justify-center z-10 ${style}`}
                      >
                        <Icon className="h-4 w-4" />
                      </div>

                      {/* label */}
                      <p
                        className={`text-[11px] leading-[14px] font-medium font-montserrat mt-1 ${
                          state === "filled" || state === "active"
                            ? "text-[var(--pos-primary-text-color)]"
                            : "text-[var(--pos-secondary-text-color)]"
                        }`}
                      >
                        {step.label}
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          {/* Order header row */}
          <div className="flex justify-between items-center">
            <div className="flex flex-col">
              <p className="font-bold text-sm leading-4 text-[var(--pos-primary-text-color)] font-figtree pb-1">
                Order ID: #{order?.orderNumber}
              </p>
              <div className="flex items-center text-xs gap-1">
                <MapPin className="h-4 w-4 text-[var(--pos-placeholder-color)]" />
                <span className="text-xs leading-5 font-regular text-[var(--pos-secondary-text-color)]">
                  {order?.location?.name}{" "}
                  {order?.locationDetails && `. ${order?.locationDetails}`}
                </span>
              </div>
            </div>

            {(() => {
              const statusKey = order?.status?.toLowerCase() || "pending";
              const status = STATUS_MAP[statusKey];

              return (
                <span
                  className={`text-xs leading-[14px] font-semibold font-figtree flex items-center gap-1 ${
                    status?.textColor || "text-yellow-600"
                  }`}
                >
                  {status && <status.Icon className="h-4 w-4" />}
                  {status?.label || order?.status}
                </span>
              );
            })()}
          </div>

          {/* Special Notes */}
          {order.notes && (
            <div className="mt-3 p-2 bg-yellow-900/20 border border-yellow-700/40 rounded">
              <span className="text-xs font-medium text-yellow-400">Note: </span>
              <span className="text-xs text-yellow-400">{order.notes}</span>
            </div>
          )}

          {/* ITEMS LIST */}
          <div className="border-y border-dashed border-[var(--pos-layout-border-color)] pt-3 mt-3 mb-3">
            {order.items?.length ? (
              order.items.map((item, index) => {
                // IMAGE LOGIC
                const imageSrc = item.product?.imageUrl
                        ? item.product.imageUrl
                        :  item.customDrink?.components?.find((c) => c.type === "spirit" && c.imageUrl)?.imageUrl || placeholder;
                return (
                  <div key={index} className="flex items-center gap-3 pb-3">
                    {/* IMAGE */}
                    <img
                      src={imageSrc}
                      alt={
                        item.product?.name ||
                        item.customDrink?.name ||
                        "Drink image"
                      }
                      className="h-[42px] w-[42px] rounded-lg object-cover flex-shrink-0"
                    />

                    {/* TEXT */}
                    <div className="flex flex-col gap-1">
                      {/* NAME */}
                      <p className="text-sm font-figtree leading-4 font-medium text-[var(--pos-primary-text-color)] mb-0">
                        {item.quantity} x{" "}
                        {item.product
                            ? (
                            <>
                              {item?.product.name}
                              {item?.product?.isRaw && <span className="text-[--pos-primary-color] ml-1">*</span>}
                            </>
                          ) : (
                            item.customDrink?.name
                          )}
                      </p>

                      {/* MODIFIERS */}
                      {item.modifiers && item.modifiers.length > 0 && (
                        <p className="text-xs font-figtree leading-[14px] font-medium text-[var(--pos-secondary-text-color)]">
                          {item.modifiers.map((mod, idx) => (
                            <span key={mod.id}>
                              {mod.modifierName}
                              {Number(mod.priceAdjustmentUsd) !== 0 && (
                                <span className="text-[var(--pos-primary-color)]">
                                  {Number(mod.priceAdjustmentUsd) > 0 ? ` (+${formatAmount(Number(mod.priceAdjustmentUsd))})` : ` (-${formatAmount(Math.abs(Number(mod.priceAdjustmentUsd)))})`}
                                </span>
                              )}
                              {idx < item.modifiers!.length - 1 ? ", " : ""}
                            </span>
                          ))}
                        </p>
                      )}

                      {/* SUB LABEL */}
                      {item.product ? (
                        <div className="flex gap-1 items-center">
                          {item?.product?.isSpicy && (
                            <Flame className="w-4 h-4 text-[#E35151] fill-[#E35151]" />
                          )}
                          {item?.product?.isVegan && (
                            <Leaf className="w-4 h-4 text-[#22C55E] fill-[#22C55E]" />
                          )}
                          {item?.product?.isSignature && (
                            <Flag className="w-4 h-4 text-[#E35151] fill-[#E35151]" />
                          )}
                          <p className="text-xs font-figtree leading-[14px] font-medium text-[var(--pos-secondary-text-color)]">
                            {item?.product?.isSignature && "Signature"}
                          </p>
                        </div>
                      ) : (
                        <p className="text-[11px] text-green-600">
                          Custom Drink
                        </p>
                      )}
                    </div>
                  </div>
                );
              })
            ) : (
              <p className="text-xs text-[var(--pos-secondary-text-color)]">No items</p>
            )}
          </div>

          <div className="flex items-center gap-1 text-xs leading-[14px] font-medium font-figtree text-[var(--pos-secondary-text-color)]">

            <span>
              Order placed on{" "}
              {new Date(order.createdAt).toLocaleDateString(undefined, {
                month: "short",
                day: "2-digit",
                year: "numeric",
              })}{" "}
              @{" "}
              {new Date(order.createdAt).toLocaleTimeString([], {
                hour: "numeric",
                minute: "2-digit",
              })}
            </span>
          </div>

          {/* Transaction Link */}
          {order.transactionId && (
            <button
              type="button"
              onClick={() => handleTransactionClick(order.transactionId!)}
              className="mt-3 flex items-center gap-1.5 text-xs font-medium font-figtree text-[#BABABB] hover:text-[#ffffff] transition-colors"
            >
              <span>Transaction #{order.transactionId}</span>
              <ExternalLink className="h-3 w-3" />
            </button>
          )}
        </div>
        <div className="bg-[var(--pos-primary-background-color)] rounded-sm py-4 px-3 flex flex-col">
          <h3 className="mb-4 text-[16px] leading-[18px] font-figtree font-semibold text-[var(--pos-primary-text-color)]">Bill Summary</h3>
          <div className="flex items-center justify-between">
            <span className="text-[13px] font-figtree leading-4 font-medium text-[var(--pos-secondary-text-color)]">Subtotal</span>
            <span className="text-[13px] font-figtree leading-4 font-medium text-[var(--pos-secondary-text-color)]">
              {formatAmount(parseFloat(order?.subtotal || order?.totalUsd))}
            </span>
          </div>

          {/* Discount Display */}
          {!!(order?.discountAmount && parseFloat(order.discountAmount) > 0) && (
            <div className="flex items-center justify-between mt-2">
              <span className="text-[13px] font-figtree leading-4 font-medium text-[#22C55E]">
                Discount{order?.discountCode && ` (${order.discountCode})`}
              </span>
              <span className="text-[13px] font-figtree leading-4 font-medium text-[#22C55E]">
                -{formatAmount(parseFloat(order.discountAmount!))}
              </span>
            </div>
          )}

          {/* Tax Breakdown */}
          {Array.isArray(order?.taxBreakdown) && order.taxBreakdown.length > 0 && (
            <>
              {(order.taxBreakdown as Array<{ type: string; rate: number; amount: number; description: string }>).map((fee, index) => (
                <div key={index} className="flex items-center justify-between mt-2">
                  <span className="text-[13px] font-figtree leading-4 font-medium text-[var(--pos-secondary-text-color)]">
                    {(fee.rate * 100).toFixed(0)}% {fee.type?.replace(/[_-]+/g, " ")?.toLowerCase()?.replace(/\b\w/g, char => char.toUpperCase())}
                  </span>
                  <span className="text-[13px] font-figtree leading-4 font-medium text-[var(--pos-secondary-text-color)]">
                    {formatAmount(fee.amount)}
                  </span>
                </div>
              ))}
            </>
          )}

          <div className="border-t border-dashed border-[var(--pos-layout-border-color)] mt-3 pt-3 flex items-center justify-between">
            <span className="text-sm leading-[18px] font-figtree font-bold text-[var(--pos-primary-text-color)]">Total</span>
            <span className="text-sm leading-[18px] font-bold text-[var(--pos-primary-text-color)] font-figtree">
              {isCard
                ? `$${order?.totalUsd}`
                : `${parseFloat(order?.totalSech ?? "0").toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 4 })} SECH`
              }
            </span>
          </div>

          {/* Payment Method */}
          <div className="border-t border-dashed border-[var(--pos-layout-border-color)] mt-3 pt-3 flex items-center justify-between">
            <span className="text-[13px] font-figtree leading-4 font-medium text-[var(--pos-secondary-text-color)]">Payment Method</span>
            {isCard ? (
              <div className="flex items-center gap-1">
                <CardBrandIcon brand={order.cardBrand} width={28} />
                {order.cardLast4 && (
                  <span className="text-[13px] font-figtree leading-4 font-medium text-[var(--pos-secondary-text-color)]">
                    ••••{order.cardLast4}
                  </span>
                )}
              </div>
            ) : (
              <div className="flex items-center gap-1">
                <Wallet className="h-4 w-4 text-[#258750]" />
                {order.walletAddress && (
                  <span className="text-[13px] font-figtree leading-4 font-medium text-[#258750]">
                    {order.walletAddress.slice(0, 6)}...{order.walletAddress.slice(-4)}
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      <TransactionDetailDialog
        transaction={selectedTransaction}
        open={selectedTransaction !== null}
        onOpenChange={(open) => { if (!open) setSelectedTransaction(null) }}
      />
    </div>
  );
}
